import { ChatBubbleLeftIcon, LinkIcon } from "@heroicons/react/20/solid";
import { useProject } from "@/lib/project";
import type { TaskListItem } from "@/lib/types";
import { cn, formatDuration, taskKey, totalSeconds } from "@/lib/utils";
import { Tag } from "../ui";
import { AssigneeList, RunningDot } from "./Assignees";

export function TaskCard({
  task,
  onOpen,
  dragging,
  overlay,
  showEpic,
  epicTitle,
}: {
  task: TaskListItem;
  onOpen?: () => void;
  dragging?: boolean;
  overlay?: boolean;
  showEpic?: boolean;
  epicTitle?: string;
}) {
  const { bundle, propsFor, assigneeStatusById } = useProject();
  const running = task.time.some((t) => t.openSince);
  const seconds = totalSeconds(task.time);
  const props = propsFor("task").filter((p) => p.type !== "date");
  const blocked = task.assignees
    .map((a) => (a.statusId ? assigneeStatusById.get(a.statusId) : undefined))
    .filter((s) => !!s);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === "Enter" && onOpen?.()}
      className={cn(
        "group cursor-pointer rounded-lg border border-line bg-surface p-2.5 text-start shadow-[0_1px_2px_rgba(0,0,0,0.08)] transition-colors select-none hover:bg-[color-mix(in_srgb,var(--surface),var(--fg)_4%)]",
        dragging && "opacity-40",
        overlay && "rotate-2 shadow-pop",
      )}
    >
      <div className="text-[14px] leading-snug font-medium break-words" dir="auto">
        {task.title}
      </div>
      {showEpic && epicTitle && (
        <div className="mt-0.5 truncate text-xs text-faint" dir="auto">
          {epicTitle}
        </div>
      )}
      <div className="mt-2 flex flex-col items-start gap-1.5">
        {props.map((p) => {
          const vals = (task.values[p.id] ?? []).map((id) => p.options.find((o) => o.id === id)).filter((o) => !!o);
          if (!vals.length) return null;
          return (
            <div key={p.id} className="flex max-w-full flex-wrap gap-1">
              {vals.map((o) => (
                <Tag key={o.id} color={o.color}>
                  {o.label}
                </Tag>
              ))}
            </div>
          );
        })}
        {blocked.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {[...new Map(blocked.map((s) => [s.id, s])).values()].map((s) => (
              <Tag key={s.id} color={s.color} className="rounded-full">
                {s.name}
              </Tag>
            ))}
          </div>
        )}
        <AssigneeList task={task} />
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-faint">
        <span className="font-mono">{taskKey(bundle.project.key, task.number)}</span>
        {task.commentCount > 0 && (
          <span className="flex items-center gap-1">
            <ChatBubbleLeftIcon className="size-3.5" /> {task.commentCount}
          </span>
        )}
        {task.prLinkCount > 0 && (
          <span className="flex items-center gap-1">
            <LinkIcon className="size-3.5" /> {task.prLinkCount}
          </span>
        )}
        {seconds > 0 && (
          <span className="ms-auto flex items-center gap-1.5 tabular-nums" title="Total time on task">
            {running && <RunningDot />}
            {formatDuration(seconds)}
          </span>
        )}
      </div>
    </div>
  );
}
